import { ProbabilityLogic } from './SelectingAndClause'

export type ProbabilityOperator = {
  logic: ProbabilityLogic
  appearancesInDeck: number
  successInDraw: number
}

export function invert(probability: number): number {
  return 1 - probability
}

/**
 * Math
 *
 * @param {number} r - number for factorial (AKA 5*4*3*2*1 || 5!)
 */
export function factorial(r: number): number {
  let value = 1
  for (let i = 2; i <= r; i++) value *= i
  return value
}

/**
 * This is a math deal.
 *
 * Mathimatical formula for clairity: n!/((n-k)!k!)
 *
 * @param {number} n -
 * @param {number} k -
 */
export function binomialCoefficient(n: number, k: number): number {
  const N = factorial(n)
  const K = factorial(k)

  return N / (factorial(n - k) * K)
}

/**
 *
 *  * Hypergeometric Probability Distribution - It's math. for more info @see
 *
 * [Wikipiedia](https://en.wikipedia.org/wiki/Hypergeometric_distribution)
 *
 * Computes the probability of exactly k successes in n draws
 * from a finite population of size N with exactly K successes.
 *
 * @param {number} N - Total population size (eg: Deck size).
 * @param {number} K - Total number of successes in the population (eg: # of cards in a category).
 * @param {number} n - Number of draws (eg: starting hand size of 7).
 * @param {number} k - Number of observed successes. (eg: # of cards drawn that are in the category).
 *
 * NOTE - this function returns the probability of exactly k successes. (eg: Player draws exactly 2 ramp cards)
 * if you want "at least 2 ramp cards", you want the cumulative probability.
 *
 * @returns {number} The probability of exactly k successes.
 */
export function hypergeometricProbability(N: number, K: number, n: number, k: number) {
  // Literally impossible
  if (k < 0 || k > K || n < 0 || n > N || n - k > N - K) return 0 // Invalid parameters

  // Compute the probability
  const numerator = binomialCoefficient(K, k) * binomialCoefficient(N - K, n - k)
  const denominator = binomialCoefficient(N, n)

  return numerator / denominator
}

/**
 * Computes the cumulative probability P(X >= k) for a hypergeometric distribution.
 *
 * @param {number} N - Total population size.
 * @param {number} K - Total number of successes in the population.
 * @param {number} n - Number of draws.
 * @param {number} k - Minimum number of observed successes.
 *
 * @returns {number} The cumulative probability of at least k successes.
 */
export function cumulativeHypergeometricProbability(N: number, K: number, n: number, k: number) {
  // 0% chance of getting more successes than there are cards in the deck
  if (k < 0 || k > K || n < 0 || n > N) return 0 // Invalid parameters

  // Compute the cumulative probability P(X >= k)
  let cumulativeProb = 0

  for (let i = k; i <= Math.min(n, K); i++) {
    cumulativeProb += hypergeometricProbability(N, K, n, i)
  }

  return cumulativeProb
}

/**
 *
 * @param N - deck size
 * @param n - total cards drawn
 */
export function exactlyXProbability(N: number, n: number, operators: ProbabilityOperator[]): number {
  let totalPotentialSuccessesInPool = 0
  let totalSuccessDrawn = 0

  for (const operator of operators) {
    totalPotentialSuccessesInPool = totalPotentialSuccessesInPool + operator.appearancesInDeck
    totalSuccessDrawn = totalSuccessDrawn + operator.successInDraw
  }

  if (totalSuccessDrawn > n) return 0

  let logicCoefficientsProduct = 1

  for (const operator of operators) {
    logicCoefficientsProduct =
      logicCoefficientsProduct * binomialCoefficient(operator.appearancesInDeck, operator.successInDraw)
  }

  // prettier-ignore
  const numerator = logicCoefficientsProduct * binomialCoefficient(N - totalPotentialSuccessesInPool, n - totalSuccessDrawn)
  const denominator = binomialCoefficient(N, n)

  return numerator / denominator
}

function exactProbability(N: number, n: number, vars: [number, number][]) {
  let totalPotentialSuccessesInPool = 0
  let totalSuccessDrawn = 0

  for (const v of vars) {
    totalPotentialSuccessesInPool = totalPotentialSuccessesInPool + v[0]
    totalSuccessDrawn = totalSuccessDrawn + v[1]
  }

  if (totalSuccessDrawn > n) return 0

  let product = 1

  for (const v of vars) {
    product = product * binomialCoefficient(v[0], v[1])
  }

  // prettier-ignore
  const numerator = product * binomialCoefficient(N - totalPotentialSuccessesInPool, n - totalSuccessDrawn)
  const denominator = binomialCoefficient(N, n)

  return numerator / denominator
}

export function exactlyProbabilityAnded(N: number, n: number, operators: ProbabilityOperator[]): number {
  let probability = 0

  if (operators.length === 1) return exactlyXProbability(N, n, operators)

  for (let i = 0; i < operators.length; i++) {
    const successes = operators[i].successInDraw

    for (let j = 0; j < n - successes; j++) {
      probability += exactlyProbabilityAnded(
        N,
        n,
        operators.filter((_, index) => i < index),
      )
    }
  }

  return 1 - probability
}
